import React, { useState, useCallback } from "react";
import { CheckCircle, Upload, Loader2 } from "lucide-react";
import cedenteDocumentoService from "../../services/cedenteDocumento.service";
import { formatCPF, formatPhoneBRLive } from "../../lib/utils";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = "application/pdf,image/jpeg,image/jpg,image/png";

function FileSlot({ label, file, error, onChange, disabled }) {
  const inputId = `upload-${label.replace(/\s+/g, "-")}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <label
        htmlFor={inputId}
        className={[
          "flex items-center gap-3 rounded-xl px-4 min-h-[56px] border-2 border-dashed transition-all select-none",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer touch-manipulation",
          file
            ? "border-emerald-300 bg-emerald-50"
            : "border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/50",
        ].join(" ")}
        style={{ touchAction: "manipulation", paddingTop: "14px", paddingBottom: "14px" }}
      >
        <span className={`shrink-0 ${file ? "text-emerald-500" : "text-slate-400"}`}>
          {file ? <CheckCircle className="h-5 w-5" /> : <Upload className="h-5 w-5" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className={`block text-sm truncate font-medium ${file ? "text-emerald-700" : "text-slate-500"}`}>
            {file ? file.name : "Toque para selecionar"}
          </span>
          {!file && <span className="block text-xs text-slate-400 mt-0.5">PDF, JPG ou PNG · máx. 10 MB</span>}
        </span>
        {file && (
          <span className="text-xs bg-emerald-100 text-emerald-700 font-semibold px-2 py-0.5 rounded-full shrink-0">✓</span>
        )}
      </label>
      <input
        id={inputId}
        type="file"
        accept={ACCEPTED_TYPES}
        className="sr-only"
        onChange={onChange}
        disabled={disabled}
      />
      {error && <p className="text-xs text-red-600 mt-0.5">{error}</p>}
    </div>
  );
}

function SuccessState({ processoNome, onClose, isModalMode }) {
  return (
    <div className="flex flex-col items-center text-center py-6 px-2">
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle className="w-9 h-9 text-emerald-600" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Documentos enviados!</h3>
      <p className="text-sm text-slate-500 mb-6 max-w-xs leading-relaxed">
        {processoNome ? (
          <>Enviados com sucesso para <strong className="text-slate-700">{processoNome}</strong>. Em breve entraremos em contato.</>
        ) : (
          "Documentos enviados com sucesso! Em breve entraremos em contato."
        )}
      </p>
      {onClose && (
        <button
          onClick={onClose}
          className="px-8 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 active:scale-[0.98] transition-all min-h-[48px]"
          style={{ touchAction: "manipulation" }}
        >
          {isModalMode ? "Fechar e continuar" : "Voltar"}
        </button>
      )}
    </div>
  );
}

export function EnvioDocumentosUploader({
  processoId,
  processoNome,
  userData,
  onClose,
  onSuccess,
}) {
  const isModalMode = Boolean(userData);

  const [form, setForm] = useState({
    nome: userData?.nomeCompleto || "",
    cpf: userData?.cpf || "",
    telefone: userData?.telefone || "",
    email: userData?.email || "",
  });
  const [ctps, setCtps] = useState(null);
  const [ppp, setPpp] = useState(null);
  const [ctpsError, setCtpsError] = useState("");
  const [pppError, setPppError] = useState("");
  const [filesError, setFilesError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleTextChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleCpfChange = useCallback((e) => {
    setForm(prev => ({ ...prev, cpf: formatCPF(e.target.value) }));
  }, []);

  const handlePhoneChange = useCallback((e) => {
    setForm(prev => ({ ...prev, telefone: formatPhoneBRLive(e.target.value) }));
  }, []);

  const handleFileChange = useCallback((setter, setError) => (e) => {
    const file = e.target.files?.[0] ?? null;
    setError("");
    setFilesError("");
    if (file && file.size > MAX_FILE_BYTES) {
      setError("Arquivo excede o limite de 10 MB.");
      setter(null);
      e.target.value = "";
      return;
    }
    setter(file);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setFilesError("");

    if (!ctps && !ppp) {
      setFilesError("Envie pelo menos um documento (CTPS ou PPP).");
      return;
    }

    const slug = window.location.hostname.split(".")[0];
    const fd = new FormData();
    fd.append("nome", form.nome.trim());
    fd.append("cpf", form.cpf.replace(/\D/g, ""));
    fd.append("telefone", form.telefone.replace(/\D/g, ""));
    fd.append("email", form.email.trim());
    fd.append("sindicato_slug", slug);
    if (ctps) fd.append("ctps", ctps);
    if (ppp) fd.append("ppp", ppp);

    setSubmitting(true);
    try {
      await cedenteDocumentoService.submitRecalculo(processoId, fd);
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "string") {
        setSubmitError(detail);
      } else {
        setSubmitError("Erro ao enviar. Tente novamente mais tarde.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return <SuccessState processoNome={processoNome} onClose={onClose} isModalMode={isModalMode} />;
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      {/* Personal data — standalone mode only */}
      {!isModalMode && (
        <>
          {[
            { id: "nome", label: "Nome completo", type: "text", autoComplete: "name", placeholder: "Seu nome completo", onChange: handleTextChange },
            { id: "cpf", label: "CPF", type: "text", inputMode: "numeric", autoComplete: "off", placeholder: "000.000.000-00", maxLength: 14, onChange: handleCpfChange },
            { id: "telefone", label: "Telefone", type: "tel", inputMode: "numeric", autoComplete: "tel", placeholder: "(00) 00000-0000", onChange: handlePhoneChange },
            { id: "email", label: "E-mail", type: "email", inputMode: "email", autoComplete: "email", placeholder: "seu@email.com", onChange: handleTextChange },
          ].map(({ id, label, onChange, ...rest }) => (
            <div key={id} className="flex flex-col gap-1.5">
              <label htmlFor={id} className="text-sm font-medium text-slate-700">
                {label} <span className="text-red-500">*</span>
              </label>
              <input
                id={id}
                name={id}
                required
                value={form[id]}
                onChange={onChange}
                className="border border-slate-300 rounded-xl px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition min-h-[48px]"
                {...rest}
              />
            </div>
          ))}
        </>
      )}

      {/* Identity pill — modal mode */}
      {isModalMode && form.nome && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
          <span className="text-xs text-slate-500">Enviando como</span>
          <span className="text-xs font-semibold text-slate-800">{form.nome}</span>
          {form.cpf && (
            <>
              <span className="text-xs text-slate-300">·</span>
              <span className="text-xs text-slate-500">{form.cpf}</span>
            </>
          )}
        </div>
      )}

      {/* File slots */}
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium text-slate-700">
          Documentos <span className="text-red-500">*</span>
          <span className="font-normal text-slate-400"> — envie ao menos um</span>
        </p>
        <FileSlot
          label="Carteira de Trabalho (CTPS)"
          file={ctps}
          error={ctpsError}
          onChange={handleFileChange(setCtps, setCtpsError)}
          disabled={submitting}
        />
        <FileSlot
          label="Perfil Profissiográfico (PPP)"
          file={ppp}
          error={pppError}
          onChange={handleFileChange(setPpp, setPppError)}
          disabled={submitting}
        />
        {filesError && <p className="text-xs text-red-600">{filesError}</p>}
      </div>

      {submitError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 leading-relaxed">
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-1 flex items-center justify-center gap-2 w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed min-h-[52px]"
        style={{ touchAction: "manipulation" }}
      >
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />Enviando...</> : "Enviar documentos"}
      </button>
    </form>
  );
}
